import { PageLoader } from 'components/page-loader.js';
import { SearchBarMainEntry } from 'components/search-bar/search-bar-entry.js';
import { SearchOverlay } from 'components/search-overlay/search-overlay.js';
import { DomRefProvider } from 'page-context/domRef/domRefProvider.js';
import { StickyPositionProvider } from 'page-context/scrollStickyStateProvider/scrollStickyMain.js';
import { ReactNode, useEffect, useRef } from 'react';
import { STICKY_BAR_APP_CONTAINER } from 'utilities/consts.js';
import { getDocument } from 'utilities/window.js';

export const MainContent = ({ children }: { children?: ReactNode }) => {
  useEffect(() => {
    getDocument()?.querySelector?.('.InitialPageLoader')?.classList.remove('InitialPageLoader');
  }, []);

  // Track user activity to detect inactivity
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();

      // Refresh the page at midnight
      if (hours === 0 && minutes === 0) {
        getDocument()?.location?.reload();
        return;
      }
    }, 59_000); // Ensure always captures 00:00

    const handleActivity = () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      inactivityTimeoutRef.current = setTimeout(
        () => getDocument()?.location?.reload(),
        60_000 * 60
      );
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => getDocument()?.addEventListener(event, handleActivity));

    // Set initial timeout for inactivity
    handleActivity();

    return () => {
      clearInterval(intervalId);
      events.forEach((event) => getDocument()?.removeEventListener(event, handleActivity));
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <DomRefProvider>
        {/** Provides positioning for appbar / secondary bar and scrolling to hash tags */}
        <StickyPositionProvider>
          <PageLoader>
            <div className="App-container" data-module="appDom" id={STICKY_BAR_APP_CONTAINER}>
              {/** Dynamic Search bar that scrolls with user */}
              <SearchBarMainEntry />
              {children}
            </div>
          </PageLoader>
          <SearchOverlay />
        </StickyPositionProvider>
      </DomRefProvider>
    </>
  );
};
